import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 1000)

    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this)
    )

    const sortRadios = this.querySelectorAll('[data-mobile-sort-radio]')
    if (sortRadios.length > 0) {
      sortRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          this.syncDesktopSortDropdown(e.target.value)
          this.debouncedOnSubmit(e)
        })
      })
    }
    window.addEventListener('popstate', this.onHistoryChange.bind(this))

    this.bindActiveFacetButtonEvents()

    // Default "in stock" on
    setTimeout(() => this.setDefaultInStockFilter(), 0)
  }

  setDefaultInStockFilter() {
    const form = this.querySelector('form')
    if (!form) return
    const inStockCheckbox = form.querySelector(
      '#filter-in-stock, input[name="filter.v.availability"][value="1"]'
    )
    if (!inStockCheckbox || inStockCheckbox.disabled) return
    const params = new URLSearchParams(window.location.search)
    if (params.has('filter.v.availability')) return

    inStockCheckbox.checked = true
    const toggle = inStockCheckbox.closest('component-toggle')
    if (toggle) {
      toggle.checked = true
      toggle.querySelector('button')?.setAttribute('aria-checked', 'true')
    }
    this.onSubmitHandler({
      preventDefault: () => {},
      target: inStockCheckbox,
    })
  }

  onSubmitHandler(event) {
    event.preventDefault()

    const formData = new FormData(event.target.closest('form'))
    const searchParams = new URLSearchParams(formData).toString()
    this.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()

    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)

    const handleFiltersRendered = () => {
      this.updateQuickFiltersComponent()
      document.removeEventListener('filters-rendered', handleFiltersRendered)
    }

    document.addEventListener('filters-rendered', handleFiltersRendered, {
      once: true,
    })
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
      element.classList.toggle('disabled-link', disable)
    })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    let currentParams = new URLSearchParams(window.location.search)
    for (const [key] of [...currentParams]) {
      if (key.includes('filter.') || key === 'sort_by' || key === 'page') {
        currentParams.delete(key)
      }
    }
    const formSearchParams = new URLSearchParams(searchParams)
    formSearchParams.forEach((value, key) => {
      currentParams.append(key, value)
    })

    const currentParamsString = currentParams.toString()

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${currentParamsString}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(currentParamsString)
  }

  renderSectionContent(html, event, source) {
    this.rerenderElement('.js-product-count', html)
    this.rerenderElement('.js-filter-count', html)
    this.rerenderElement('.js-product-grid-list', html)
    this.rerenderElement('.js-collection-load-more', html)
    this.rerenderElement('.js-load-more-container', html)
    this.rerenderElement('.js-filter-drawer-header', html)
    this.rerenderElement('.js-filter-drawer-footer', html)
    this.rerenderQuickFilters(html)
    this.renderFilters(html, event)

    requestAnimationFrame(() => {
      document.dispatchEvent(
        new CustomEvent('filters-rendered', {
          bubbles: true,
          detail: { source },
        })
      )
      // Ensure quick filters are synced after DOM updates
      this.updateQuickFiltersComponent()
    })
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderSectionContent(html, event, 'renderSectionFromFetch')
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderSectionContent(html, event, 'renderSectionFromCache')
  }

  rerenderElement(className, html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const sourceElement = parsedHTML.querySelector(className)
    if (!sourceElement) return

    const data = sourceElement.innerHTML
    const prevEls = document.querySelectorAll(className)
    prevEls.forEach((el) => (el.innerHTML = data))
  }

  updateQuickFiltersComponent() {
    const quickFiltersComponent = document.querySelector('quick-filters')
    if (quickFiltersComponent) {
      if (quickFiltersComponent.syncWithMainFilters) {
        quickFiltersComponent.syncWithMainFilters()
      }
    }
  }

  rerenderQuickFilters(html) {
    const mainQuickFilters = document.querySelector('quick-filters')
    if (mainQuickFilters) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
      const sourceMainQuickFilters = parsedHTML.querySelector('quick-filters')
      if (sourceMainQuickFilters) {
        mainQuickFilters.innerHTML = sourceMainQuickFilters.innerHTML

        const quickFiltersComponent = document.querySelector('quick-filters')
        if (quickFiltersComponent) {
          const observer = new MutationObserver(() => {
            quickFiltersComponent.checkboxes =
              quickFiltersComponent.querySelectorAll(
                '[data-quick-filter-checkbox]'
              )

            if (quickFiltersComponent.attachEventListeners) {
              quickFiltersComponent.attachEventListeners()
            }

            quickFiltersComponent.configureSlider()

            const freeSlider =
              quickFiltersComponent.querySelector('free-slider')
            const handleSliderReady = () => {
              this.updateQuickFiltersComponent()
              quickFiltersComponent.removeEventListener(
                'quick-filters-slider-ready',
                handleSliderReady
              )
            }

            quickFiltersComponent.addEventListener(
              'quick-filters-slider-ready',
              handleSliderReady,
              { once: true }
            )

            requestAnimationFrame(() => {
              if (freeSlider?.slider) {
                handleSliderReady()
              }
            })

            observer.disconnect()
          })

          observer.observe(mainQuickFilters, {
            childList: true,
            subtree: true,
          })
        }
      }
    }
  }

  renderFilters(...args) {
    this.renderSingleFormFilters('#CollectionFiltersForm', ...args)

    document.dispatchEvent(
      new CustomEvent('plp:filter-update', {
        bubbles: true,
        composed: true,
      })
    )

    // Sync quick filters after main filters are updated
    this.updateQuickFiltersComponent()
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} .js-filter`
    )
    const newFilterIds = new Set(
      Array.from(facetDetailsElements)
        .map((el) => el.dataset.filterId)
        .filter(Boolean)
    )
    const matchesFilterId = (element) =>
      element.dataset.filterId ===
      event?.target.closest('.js-filter')?.dataset.filterId

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesFilterId(element)
    )

    const countsToRender =
      Array.from(facetDetailsElements).find(matchesFilterId)

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `${form} .js-filter[data-filter-id="${element.dataset.filterId}"]`
      )
      if (target) {
        target.hidden = false
        target.innerHTML = element.innerHTML
      }
    })

    document
      .querySelectorAll(`${form} .js-filter[data-filter-id]`)
      .forEach((el) => {
        const id = el.dataset.filterId
        if (id && !newFilterIds.has(id)) {
          el.hidden = true
        }
      })

    this.renderActiveFacets(parsedHTML)

    const targetFilter = event?.target?.closest?.('.js-filter')
    if (countsToRender && targetFilter) {
      this.renderCounts(countsToRender, targetFilter)
    }
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.js-facets--mobile',
      '.js-facets--desktop',
    ]

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector)
      if (!activeFacetsElement) return
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML
    })

    this.bindActiveFacetButtonEvents()
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.js-bubble', '.js-selected']
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  syncDesktopSortDropdown(value) {
    const desktopSortRadios = document.querySelectorAll(
      '[data-desktop-sort-radio]'
    )
    desktopSortRadios.forEach((radio) => {
      if (radio.value === value && !radio.checked) {
        radio.checked = true
        radio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )

    this.updateQuickFiltersComponent()
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('CollectionProductGrid').dataset.id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
